import React, { useState, useCallback } from 'react';
import './Flashcard.css';

export type CardSize = 'standard' | 'large';

export interface FlashcardProps {
  /** Question text */
  question: React.ReactNode;
  /** Answer text */
  answer: React.ReactNode;
  /** Card size - 'large' for long-form content */
  size?: CardSize;
  /** Whether the card is currently flipped */
  flipped?: boolean;
  /** Callback when card is flipped */
  onFlip?: () => void;
  /** Additional CSS class */
  className?: string;
}

/**
 * Flashcard component with clean, minimal styling.
 * Supports standard and large sizes for different content lengths.
 */
export const Flashcard: React.FC<FlashcardProps> = ({
  question,
  answer,
  size = 'standard',
  flipped: controlledFlipped,
  onFlip,
  className = '',
}) => {
  const [internalFlipped, setInternalFlipped] = useState(false);

  const isControlled = controlledFlipped !== undefined;
  const flipped = isControlled ? controlledFlipped : internalFlipped;

  const handleFlip = useCallback(() => {
    if (isControlled) {
      onFlip?.();
    } else {
      setInternalFlipped(prev => !prev);
    }
  }, [isControlled, onFlip]);

  const classes = [
    'stc-flashcard',
    `stc-flashcard--${size}`,
    flipped && 'stc-flashcard--flipped',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={classes}
      onClick={handleFlip}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleFlip();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={
        flipped
          ? 'Showing answer, click to show question'
          : 'Showing question, click to reveal answer'
      }
    >
      {!flipped ? (
        <div className="stc-flashcard__face stc-flashcard__front">
          <div className="stc-flashcard__content stc-flashcard__question">{question}</div>
        </div>
      ) : (
        <div className="stc-flashcard__face stc-flashcard__back">
          <div className="stc-flashcard__content stc-flashcard__answer">{answer}</div>
        </div>
      )}
    </div>
  );
};
